import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { MonitorComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-component";
import {
  useQpmMonitorGetQuery,
  useQpmMonitorListQuery,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export function CreateOrEditMonitorRoute() {
  const navigate = useNavigate();
  const { monitorId, catalogId } = useParams();
  const { companyId } = useActiveCompany();

  // Catalog mode keeps using the existing component which already implements prev/next.
  const isCatalogMode = !!catalogId;

  const { data: monitor } = useQpmMonitorGetQuery(
    { id: monitorId! },
    { skip: !monitorId || isCatalogMode },
  );

  const { data: baseList } = useQpmMonitorListQuery(
    monitor
      ? {
          company: { active: true, values: [companyId] },
          catalog: { active: false, values: [] },
          type: { active: true, values: [monitor.type] },
        }
      : {},
    { skip: !monitor || isCatalogMode },
  );

  const sortedMonitorIds = useMemo(() => {
    if (!baseList?.items) return [];
    return [...baseList.items]
      .sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        }),
      )
      .map((m) => m.id);
  }, [baseList?.items]);

  const currentIndex = useMemo(() => {
    if (!monitorId || !sortedMonitorIds.length) return -1;
    return sortedMonitorIds.indexOf(monitorId);
  }, [sortedMonitorIds, monitorId]);

  const goPrev = () => {
    if (currentIndex <= 0) return;
    navigate(`../${sortedMonitorIds[currentIndex - 1]}`, { replace: true });
  };

  const goNext = () => {
    if (currentIndex < 0 || currentIndex >= sortedMonitorIds.length - 1) return;
    navigate(`../${sortedMonitorIds[currentIndex + 1]}`, { replace: true });
  };

  return (
    <MonitorComponent
      monitorId={monitorId!}
      prevNext={
        isCatalogMode
          ? undefined
          : {
              currentIndex,
              total: sortedMonitorIds.length,
              goPrev,
              goNext,
            }
      }
    />
  );
}
